import { CaseUpdateInput } from "@types-runway/case";
import { Condition, ConditionTypes } from "@types-runway/condition";
import {
  AnswerType,
  Demographics,
  EntityType,
  isDemographics,
  NoticeMessage,
  QuestionDependency,
  QuestionnaireContext,
  StoragePath,
  WarningMessage,
} from "@types-runway/questionnaire";
import { Partner } from "@types-runway/state";
import { User } from "@types-runway/user";
import { includesBirthControl } from "@utils/questionnaire/validators";
import USStates from "@utils/forms/USstates.json";
import { toSlug } from "@utils/content/toSlug";
import { isValidDateInput } from "@utils/forms/utilsFormvalidation";

export const getUsersAge = (dateOfBirth: Date): number => {
  if (isNaN(dateOfBirth.getTime())) return NaN;
  const today = new Date();
  let age = today.getFullYear() - dateOfBirth.getFullYear();
  const monthDiff = today.getMonth() - dateOfBirth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dateOfBirth.getDate())) {
    age--;
  }
  return age;
};

export const isValidAnswer = (stateKey: string, answer: AnswerType): boolean => {
  switch (stateKey) {
    case "dob": {
      const raw = answer?.toString() ?? "";
      if (!isValidDateInput(raw)) return false;
      const [month, day, year] = raw.split("/").map(Number);
      const usersAge = getUsersAge(new Date(year, month - 1, day));
      if (isNaN(usersAge) || usersAge < 13) return false;
      return true;
    }
    case "stateOfResidence": {
      const states = USStates.map((state) => state.name);
      return states.includes(answer.toString());
    }
    default:
      return true;
  }
};

export interface WarningContext {
  conditions: Condition[];
  dateOfBirth: string;
}

export const displayWarningMessage = (
  answer: AnswerType,
  warningMessage: WarningMessage | undefined,
  context: WarningContext
): NoticeMessage | null => {
  const noticeMessage: NoticeMessage | null =
    warningMessage?.variant && warningMessage?.message
      ? {
          variant: warningMessage.variant,
          message: warningMessage.message,
        }
      : null;

  switch (warningMessage?.trigger?.conditional) {
    case "syncStateOfResidence": {
      const syncState = USStates.filter(
        (state) => state.name === answer && state.isSync
      );
      return syncState.length > 0 ? noticeMessage : null;
    }
    case "dob": {
      const usersAge = getUsersAge(new Date(answer?.toString() ?? ""));
      return usersAge < 18 ? noticeMessage : null;
    }
    case "malariaPrevCost": {
      const days = (answer as CaseUpdateInput).trip?.days ?? 0;
      const malariaCondition = context.conditions.find(
        (cond) => cond.product.name === ConditionTypes.MALARIA_CONSULTATION
      );
      if (!malariaCondition || days < 7) return null;

      const medicationName = malariaCondition.suggestedMedication?.name ?? "";
      const variantKey = medicationName.toLowerCase().split(" ")[0];
      const variant = warningMessage?.messageVariants?.[variantKey];
      return variant ? { variant: variant.variant, message: variant.message } : noticeMessage;
    }
    case "birthControl":
      return includesBirthControl(answer?.toString() ?? "") ? noticeMessage : null;
    default:
      return null;
  }
};

export interface DependencyContext extends QuestionnaireContext {
  dateOfBirth: string;
  conditions: Condition[];
}

function resolveDob(
  dateOfBirth: string,
  questions: DependencyContext["questions"]
): Date | null {
  if (dateOfBirth) return new Date(dateOfBirth);
  const raw = questions?.["dob"]?.answer?.toString() ?? "";
  if (!raw) return null;
  // The HTML <input type="date"> emits ISO format ("YYYY-MM-DD"); manual text entry
  // uses "MM/DD/YYYY". new Date() handles both — guard with isNaN for garbage strings.
  const parsed = new Date(raw);
  return isNaN(parsed.getTime()) ? null : parsed;
}

export const getQuestionDependencyMatch = (
  dependency: QuestionDependency,
  context: DependencyContext
): boolean => {
  const { questions, isAuth, partner, query, dateOfBirth, conditions } = context;

  if (dependency.type === "question" && questions) {
    const dependencyAnswer = questions[dependency.question?.stateKey ?? ""]?.answer;
    if (dependencyAnswer) {
      const answerMap = new Map([
        ["false", "no"],
        ["true", "yes"],
        ["1", "male"],
        ["2", "female"],
      ]);

      const answerParts = dependencyAnswer
        .toString()
        .toLocaleLowerCase()
        .split("|")
        .map((part) => answerMap.get(part) ?? part);

      const rawExpected = dependency.question?.expectedAnswer;
      const expectedAnswers = Array.isArray(rawExpected)
        ? rawExpected.map((a) => a.toLocaleLowerCase())
        : [rawExpected?.toLocaleLowerCase() ?? ""];

      return answerParts.some((part) => expectedAnswers.includes(part));
    }
  }

  if (dependency.type === "conditional") {
    switch (dependency.conditional) {
      case "loggedInUser":
        return isAuth ? true : false;
      case "syncStateOfResidence": {
        const residencyStateQuestion = questions ? questions["stateOfResidence"].answer : "";
        const syncState = USStates.filter(
          (state) => state.name === residencyStateQuestion && state.isSync
        );
        return syncState.length > 0;
      }
      case "dob": {
        const effectiveDob = resolveDob(dateOfBirth, questions);
        if (!effectiveDob) return true;
        return Math.floor(getUsersAge(effectiveDob)) < 18;
      }
      case "requiresBiometrics": {
        const effectiveDob = resolveDob(dateOfBirth, questions);
        if (!effectiveDob) return false;
        return Math.floor(getUsersAge(effectiveDob)) <= 18;
      }
      case "insurance":
        return (partner as Partner | null)?.type !== "insurance";
      case "referralSource":
        return !partner;
      case "requiresAddons": {
        const isStreamlinedFlow = query?.get("flow") === "streamlined";
        if (isStreamlinedFlow && conditions.length > 0) {
          return false;
        }
        return true;
      }
      case "isStreamlinedFlow":
        return query?.get("flow") === "streamlined";
      case "selectedMedicationIsArakoda": {
        const malariaCondition = conditions.find(
          (c) => c.product.name === ConditionTypes.MALARIA_CONSULTATION
        );
        const medicationSlug = toSlug(malariaCondition?.suggestedMedication?.name ?? "");
        return medicationSlug.startsWith("arakoda");
      }
    }
  }

  return false;
};

export const questionIsAlreadyAsked = (
  storagePath: StoragePath<EntityType>[],
  user: User
): boolean => {
  const propertyKeys: string[] = [];

  storagePath.forEach((dataTarget) => {
    if ("nestingProperty" in dataTarget) {
      return;
    }
    if ("propertyKey" in dataTarget) {
      propertyKeys.push(dataTarget.propertyKey);
    }
  });

  const firstDemographicsIndex = propertyKeys.findIndex(isDemographics);

  if (firstDemographicsIndex > -1) {
    const today = new Date().toLocaleDateString();

    const demographicPropertyKey = propertyKeys[firstDemographicsIndex] as Demographics;
    const demographicDataPoint = user[demographicPropertyKey];

    if (demographicDataPoint && !isNaN(new Date(demographicDataPoint).getTime())) {
      return new Date(demographicDataPoint).toLocaleDateString() !== today;
    }

    return Boolean(demographicDataPoint);
  }
  return false;
};
